import { secureGetItem, secureSetItem, secureRemoveItem } from './storage';

/**
 * Generates a deterministic short code from a vehicle ID
 * Uses a hash-based approach to ensure the same ID always generates the same code
 * Format: 10 alphanumeric characters (lowercase)
 * Example: l12ab558ec
 */
export const generateShortCode = (vehicleId: string | number): string => {
  const id = vehicleId.toString();
  
  // Simple hash function (djb2)
  let hash = 5381;
  for (let i = 0; i < id.length; i++) {
    hash = ((hash << 5) + hash) + id.charCodeAt(i);
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  // Convert hash to positive number
  hash = Math.abs(hash);
  
  // Generate 5 character code using base36 (0-9, a-z)
  let code = '';
  const chars = '0123456789abcdefghijklmnopqrstuvwxyz';
  
  // Use the hash as seed for pseudo-random generation
  let seed = hash;
  for (let i = 0; i < 5; i++) {
    // Linear congruential generator for deterministic randomness
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    const index = seed % chars.length;
    code += chars[index];
  }
  
  return code;
};

/**
 * Stores and retrieves the mapping between short codes and vehicle IDs
 * Uses encrypted localStorage for persistence
 */
const STORAGE_KEY = 'vehicle-short-codes';

interface ShortCodeMap {
  [shortCode: string]: string; // shortCode -> vehicleId
}

/**
 * Gets or creates a short code for a vehicle ID
 */
export const getOrCreateShortCode = async (vehicleId: string | number): Promise<string> => {
  const id = vehicleId.toString();
  
  // Get existing mappings
  const mappings = await getShortCodeMappings();
  
  // Check if we already have a code for this ID
  const existingCode = Object.entries(mappings).find(([_, vId]) => vId === id)?.[0];
  if (existingCode) {
    return existingCode;
  }
  
  // Generate new short code
  const shortCode = generateShortCode(id);
  
  // Store the mapping
  mappings[shortCode] = id;
  await secureSetItem(STORAGE_KEY, mappings);
  
  return shortCode;
};

/**
 * Gets the vehicle ID from a short code
 */
export const getVehicleIdFromShortCode = async (shortCode: string): Promise<string | null> => {
  const mappings = await getShortCodeMappings();
  return mappings[shortCode] || null;
};

/**
 * Gets all short code mappings from localStorage
 */
const getShortCodeMappings = async (): Promise<ShortCodeMap> => {
  try {
    const stored = await secureGetItem(STORAGE_KEY);
    return stored || {};
  } catch (error) {
    console.error('Error getting mappings:', error);
    return {};
  }
};

/**
 * Clears all short code mappings (useful for testing)
 */
export const clearShortCodeMappings = async (): Promise<void> => {
  await secureRemoveItem(STORAGE_KEY);
};

/**
 * Pre-generate short codes for multiple vehicles in batch (more efficient)
 */
export const batchGenerateShortCodes = async (vehicleIds: (string | number)[]): Promise<void> => {
  // Get existing mappings
  const mappings = await getShortCodeMappings();
  
  // Generate codes for all vehicles
  let newCount = 0;
  for (const vehicleId of vehicleIds) {
    const id = vehicleId.toString();
    
    // Check if we already have a code for this ID
    const existingCode = Object.entries(mappings).find(([_, vId]) => vId === id)?.[0];
    if (!existingCode) {
      // Generate new short code
      const shortCode = generateShortCode(id);
      mappings[shortCode] = id;
      newCount++;
    }
  }
  
  // Save all mappings at once
  if (newCount > 0) {
    await secureSetItem(STORAGE_KEY, mappings);
  }
};

